import { useNavigate, useParams } from 'react-router-dom'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { F0Button } from '@factorialco/f0-react'
import {
  DetailsItemType,
  DetailsItemsList,
  ResourceHeader,
} from '@factorialco/f0-react/dist/experimental'
import {
  IntegrationsJoinCancelOrderRequestDocument,
  IntegrationsJoinOrderApproveDocument,
  IntegrationsJoinOrderRejectDocument,
  IntegrationsJoinPurchaseRequestsCollectionDocument,
  PurchaseRequestDetailPageDocument,
} from 'generated/resources/graphql'
import { compact } from 'lodash'

import Sidepanel from 'modules/core/components/Sidepanel'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import buildUrl from 'modules/core/lib/buildUrl'
import { dateBackendDateTimeToUI } from 'modules/core/lib/dates'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { getOrderStatusColor, getPremiumOfferTypeLabel } from 'modules/ats/lib/utils/premiumPortals'
import { usePricingUtils } from 'modules/ats/pages/JobPostingMarketplace/usePricingUtils'

const PurchaseRequestDetailPage = () => {
  const { orderId } = useParams()

  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const { access } = useCurrentSession()
  const { formatPrice } = usePricingUtils()
  const navigateToPurchaseRequests = () => navigate(navigation.ats.purchaseRequests.fullPath())

  const { data: pageData } = useQuery(PurchaseRequestDetailPageDocument, {
    variables: {
      id: Number(orderId),
    },
    skip: !Number(orderId),
  })

  const [approveOrder, { loading: approving }] = useMutation(IntegrationsJoinOrderApproveDocument, {
    extractorFn: (data) => data.integrationsJoinMutations.approveIntegrationsjoinOrder,
    refetchQueries: [IntegrationsJoinPurchaseRequestsCollectionDocument],
  })

  const [rejectOrder, { loading: rejecting }] = useMutation(IntegrationsJoinOrderRejectDocument, {
    extractorFn: (data) => data.integrationsJoinMutations.rejectIntegrationsjoinOrder,
    refetchQueries: [IntegrationsJoinPurchaseRequestsCollectionDocument],
  })

  const [cancelOrder, { loading: cancelling }] = useMutation(
    IntegrationsJoinCancelOrderRequestDocument,
    {
      extractorFn: (data) => data.integrationsJoinMutations.cancelIntegrationsjoinOrder,
      refetchQueries: [IntegrationsJoinPurchaseRequestsCollectionDocument],
    }
  )

  const handleCancelOrderRequest = async () => {
    if (!order || !isCancellable) return

    const result = await cancelOrder({ variables: { id: order.id, accessId: access.id } })

    const handled = handleResult(result, (error) => {
      app.setFlash({
        type: 'wrong',
        message: error.messages ?? i18n.t('common.error'),
      })
    })

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('ats.purchase_requests_page.sidepanel.request_cancelled'),
      })

      navigateToPurchaseRequests()
    }

    return handled
  }

  const handleApproveOrderRequest = async () => {
    if (!order || !isApprover) return

    const result = await approveOrder({ variables: { id: order.id, accessId: access.id } })

    const handled = handleResult(result, (error) => {
      app.setFlash({
        type: 'wrong',
        message: error.messages ?? i18n.t('common.error'),
      })
    })

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('ats.purchase_requests_page.sidepanel.request_approved'),
      })

      navigateToPurchaseRequests()
    }

    return handled
  }
  const handleRejectOrderRequest = async () => {
    if (!order || !isApprover) return

    const result = await rejectOrder({
      variables: { id: order.id, accessId: access.id, reason: 'Rejected by Approver' },
    })

    const handled = handleResult(result, (error) => {
      app.setFlash({
        type: 'wrong',
        message: error.messages ?? i18n.t('common.error'),
      })
    })

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('ats.purchase_requests_page.sidepanel.request_rejected'),
      })
      navigateToPurchaseRequests()
    }

    return handled
  }

  const order = pageData?.integrationsJoin?.ordersConnection?.nodes[0]
  const canNavigateToJobs = pageData?.ats?._policies?.manageJobPostings?.can

  if (!order) {
    navigateToPurchaseRequests()
    return
  }
  const isRequestOwner = access.id === order.requestedBy.id
  const isOrderCancelled = order.status === 'cancelled'
  const isOrderApproved = order.status === 'approved'
  const isCancellable = isRequestOwner && !isOrderCancelled && !isOrderApproved

  const approvalSteps = order.approval?.steps

  // If the owner is part of the approval group
  // the flow will be approved and others member status will be "ignored"
  const pendingApprovalAccesses = approvalSteps?.flatMap((step) =>
    step.stepApprovers
      .filter((approver) => approver.status === 'pending')
      .map((approver) => approver.access)
  )
  const approverAccess = approvalSteps?.map(
    (step) => step.stepApprovers.find((approver) => approver.status === 'approved')?.access
  )[0]
  const rejecterAccess = approvalSteps?.map(
    (step) => step.stepApprovers.find((approver) => approver.status === 'rejected')?.access
  )[0]

  const pendingForApproval = pendingApprovalAccesses && pendingApprovalAccesses.length > 0
  const isApprover =
    pendingApprovalAccesses &&
    pendingApprovalAccesses.some((approver) => approver?.id === access.id)

  const approvalDetail = (): DetailsItemType[] => {
    return compact([
      pendingForApproval
        ? {
            title: i18n.t('ats.purchase_requests_page.common.pending_approval'),
            content: {
              type: 'avatar-list',
              avatarList: {
                size: 'sm',
                max: 4,
                type: 'person',
                avatars: pendingApprovalAccesses.map((pendingApprovalAccess) => ({
                  firstName: pendingApprovalAccess?.fullName ?? '',
                  lastName: '',
                  src: buildUrl(pendingApprovalAccess?.avatar) ?? undefined,
                })),
              },
            },
          }
        : null,
      approverAccess
        ? {
            title: i18n.t('ats.purchase_requests_page.common.approved_by'),
            content: {
              type: 'person',
              firstName: approverAccess?.fullName ?? '',
              lastName: '',
              avatarUrl: buildUrl(approverAccess?.avatar) ?? undefined,
            },
          }
        : null,
      rejecterAccess
        ? {
            title: i18n.t('ats.purchase_requests_page.common.rejected_by'),
            content: {
              type: 'person',
              firstName: rejecterAccess?.fullName ?? '',
              lastName: '',
              avatarUrl: buildUrl(rejecterAccess?.avatar) ?? undefined,
            },
          }
        : null,
    ])
  }

  return (
    <Sidepanel
      title={order.atsJobPosting.title}
      footer={
        <Box gap="s8" flexDirection="row">
          {isCancellable && (
            <F0Button
              label={i18n.t('ats.purchase_requests_page.sidepanel.actions.cancel')}
              onClick={handleCancelOrderRequest}
              disabled={cancelling}
            />
          )}

          {!isRequestOwner && !isOrderCancelled && isApprover && (
            <>
              <F0Button
                variant="outline"
                onClick={handleRejectOrderRequest}
                loading={rejecting}
                label={i18n.t('ats.purchase_requests_page.sidepanel.actions.reject')}
              />
              <F0Button
                onClick={handleApproveOrderRequest}
                loading={approving}
                label={i18n.t('ats.purchase_requests_page.sidepanel.actions.approve')}
              />
            </>
          )}
        </Box>
      }
      width="wide"
      closeLabel={i18n.t('common.close')}
      onClose={navigateToPurchaseRequests}
      overlay
      boxPadding
      open
      headerBorder
      locked
    >
      <Box gap="s24">
        <ResourceHeader
          title={order.premiumOfferName}
          status={{
            label: order.statusLabel,
            text: order.statusLabel,
            variant: getOrderStatusColor(order.status),
          }}
          metadata={[
            {
              label: i18n.t('ats.purchase_requests_page.table.headers.requested_at'),
              value: { type: 'text', content: dateBackendDateTimeToUI(order.createdAt) },
            },
          ]}
        />
        <DetailsItemsList
          tableView
          details={[
            {
              title: i18n.t('ats.purchase_requests_page.common.requested_by'),
              content: {
                type: 'person',
                firstName: order.requestedBy.firstName,
                lastName: order.requestedBy.lastName,
                avatarUrl: buildUrl(order.requestedBy.avatar) ?? undefined,
              },
            },
            {
              title: i18n.t('ats.purchase_requests_page.sidepanel.status_detail'),
              content: {
                type: 'item',
                text: order.statusDetail,
              },
            },
            ...approvalDetail(),
            {
              title: i18n.t('ats.purchase_requests_page.common.job_posting'),
              content: {
                type: 'item',
                text: order.atsJobPosting.title,
                action: canNavigateToJobs
                  ? {
                      type: 'navigate',
                      href: navigation.ats.jobs.jobsId.fullPath({
                        jobId: order.atsJobPosting.id,
                      }),
                    }
                  : undefined,
              },
            },
            {
              title: i18n.t('ats.purchase_requests_page.sidepanel.portals'),
              content: {
                type: 'avatar-list',
                avatarList: {
                  size: 'sm',
                  max: 4,
                  type: 'company',
                  avatars: order.snapshot?.products.map((product) => ({
                    name: product.jobBoard.name,
                    src: product.jobBoard.logoUrl ?? undefined,
                  })),
                },
              },
            },
            {
              title: i18n.t('ats.purchase_requests_page.common.offer_type'),
              content: {
                type: 'item',
                text: getPremiumOfferTypeLabel(order.premiumOfferType),
              },
            },
            {
              title: i18n.t('ats.purchase_requests_page.common.price'),
              content: {
                type: 'item',
                text: formatPrice(order.snapshot?.price, order.snapshot?.currency),
              },
            },
          ]}
        />

        {order.note && (
          <Box gap="s8">
            <Text.Normal size="200" color="grey700">
              Comments
            </Text.Normal>
            <Box paddingX="s12" paddingY="s8" background="grey200" borderRadius={{ all: 'abs012' }}>
              <Text.Normal size="200" color="grey1000">
                {order.note}
              </Text.Normal>
            </Box>
          </Box>
        )}
      </Box>
    </Sidepanel>
  )
}

export default PurchaseRequestDetailPage
